import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  PencilIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { ItemStatus } from "../../Utils";
import { TripletexSyncBadge } from "../tripletex/tripletex-sync-badge";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { PMTabs } from "../../utils/PMTabs";
import { ProjectStatusTagsControl } from "./project-status-tags";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { ProjectOverflowMenu } from "./ProjectOverflowMenu";
import { HistoriesModalButton } from "../histories/modal-button";
import { projectPaletteShortcutLabel } from "./ProjectCommandPalette";
import {
  applyHeroSlotMinHeight,
  compensateScrollTop,
  nextHeroCompact,
  readHeroFlowHeight,
  readScrollerMetrics,
  scrollParentOf,
} from "./projectHeroCompact";

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("nb-NO");
}

function initials(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function ProjectViewHero({
  project,
  onRefresh,
  tabs,
  selectedTab,
  onSelectTab,
  hideMobileSelect,
  onOpenPalette,
  onEdit,
  onExportPdf,
  exportingPdf,
  canEdit,
  overflowItems,
  onRetrySync,
  onSwitchProject,
}) {
  const { t } = useTranslation();
  const slotRef = useRef(null);
  const heroRef = useRef(null);
  const compactRef = useRef(false);
  const scrollerRef = useRef(null);
  const pendingHeightRef = useRef(null);
  const [compact, setCompact] = useState(false);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    applyHeroSlotMinHeight(slot, 0);
    const before = pendingHeightRef.current;
    if (before == null) return;
    pendingHeightRef.current = null;
    const after = readHeroFlowHeight(slot);
    compensateScrollTop(scrollerRef.current, before - after);
  }, [compact]);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;

    applyHeroSlotMinHeight(slot, 0);

    let scroller = scrollParentOf(slot);
    scrollerRef.current = scroller;
    let resizeObserver = null;
    let applyCompact = () => {};

    const unbindScroll = () => {
      scroller?.removeEventListener("scroll", applyCompact);
    };

    const bindScroller = (next) => {
      if (!next || next === scroller) return;
      unbindScroll();
      if (resizeObserver && scroller && scroller !== window) {
        try {
          resizeObserver.unobserve(scroller);
        } catch {
          /* node may already be gone */
        }
      }
      scroller = next;
      scrollerRef.current = scroller;
      scroller.addEventListener("scroll", applyCompact, { passive: true });
      if (resizeObserver && scroller !== window) {
        resizeObserver.observe(scroller);
      }
    };

    applyCompact = () => {
      bindScroller(scrollParentOf(slot));
      const { scrollY } = readScrollerMetrics(scroller);
      const next = nextHeroCompact(scrollY, compactRef.current);
      if (next === compactRef.current) return;

      pendingHeightRef.current = readHeroFlowHeight(slot);
      compactRef.current = next;
      setCompact(next);
    };

    scroller.addEventListener("scroll", applyCompact, { passive: true });
    window.addEventListener("resize", applyCompact);
    resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(applyCompact);
    if (resizeObserver && scroller !== window) {
      resizeObserver.observe(scroller);
    }

    applyCompact();

    return () => {
      unbindScroll();
      window.removeEventListener("resize", applyCompact);
      resizeObserver?.disconnect();
    };
  }, []);

  const copyTitle =
    (
      project?.displayTitle ||
      [project?.project_number, project?.name].filter(Boolean).join(" ")
    )?.trim() || "";

  const handleCopyTitle = async () => {
    if (!copyTitle) return;
    try {
      await copyTextToClipboard(copyTitle);
      toast.success(t("project.copy.success"));
    } catch {
      toast.error(t("project.copy.failed"));
    }
  };

  const pmName = project?.project_manager?.displayTitle || project?.project_manager?.name;
  const meta = [
    (project?.customer?.displayTitle || project?.customer?.name)
      ? `${t("project.view.customer")}: ${project.customer.displayTitle || project.customer.name}`
      : null,
    project?.project_category?.displayTitle,
    project?.department?.displayTitle,
    project?.start_date
      ? `${t("project.overview.fact.start")} ${formatDate(project.start_date)}`
      : t("project.overview.start_missing"),
    project?.end_date
      ? `${t("project.overview.fact.end")} ${formatDate(project.end_date)}`
      : t("project.overview.end_unset"),
  ].filter(Boolean);

  return (
    <div
      ref={slotRef}
      className={compact ? "pv-hero-slot pv-hero-slot--compact" : "pv-hero-slot"}
    >
      <div
        ref={heroRef}
        className={compact ? "pv-hero pv-hero--compact" : "pv-hero"}
      >
        <div className="pv-hero-top">
          <div className="pv-hero-main">
            <div className="pv-title-row">
              {project?.project_number ? (
                <span className="pv-number">{project.project_number}</span>
              ) : null}
              <ProjectSwitcher currentId={project?.id} onSelect={onSwitchProject}>
                {({ open, toggle }) => (
                  <button
                    type="button"
                    className={`pv-switch${open ? " pv-switch--open" : ""}`}
                    onClick={toggle}
                    aria-expanded={open}
                    aria-haspopup="dialog"
                    title={t("project.switcher.switch")}
                  >
                    <h1 className="pv-title">{project?.name || project?.displayTitle}</h1>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#6b7684" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M4 6l4 4 4-4" />
                    </svg>
                  </button>
                )}
              </ProjectSwitcher>
              {copyTitle ? (
                <button
                  type="button"
                  className="pv-copy"
                  onClick={handleCopyTitle}
                  title={t("project.view.copy_title")}
                  aria-label={t("project.view.copy_title")}
                >
                  <ClipboardDocumentIcon className="h-5 w-5" aria-hidden />
                </button>
              ) : null}
              <div className="relative z-20 inline-flex shrink-0 flex-nowrap items-center gap-2">
                <ItemStatus item={project} />
                {project?.tripletex_sync?.enabled ? (
                  <TripletexSyncBadge
                    sync={project.tripletex_sync}
                    interactive
                    onRetry={onRetrySync}
                  />
                ) : null}
              </div>
            </div>

            <div className="pv-tags relative z-20 flex flex-wrap items-center gap-2">
              <ProjectStatusTagsControl project={project} onSaved={onRefresh} />
            </div>

            <div className="pv-meta">
              {meta.map((item, index) => (
                <span key={`${item}-${index}`} className="inline-flex items-center gap-4">
                  {index > 0 ? <span className="pv-dot">•</span> : null}
                  {item}
                </span>
              ))}
              {pmName ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <span className="pv-dot">•</span>
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full bg-[#e4eefb] text-[9px] font-bold text-[#1463C7]"
                    aria-hidden
                  >
                    {initials(pmName)}
                  </span>
                  {t("project.manager")}{" "}
                  <strong className="font-semibold text-[#1b2430]">{pmName}</strong>
                </span>
              ) : null}
            </div>
          </div>

          <div className="pv-actions">
            <button
              type="button"
              className="pv-btn pv-btn-search hidden lg:inline-flex"
              onClick={onOpenPalette}
            >
              <MagnifyingGlassIcon className="h-4 w-4" aria-hidden />
              <span className="pv-btn-label">{t("project.palette.trigger")}</span>
              <span className="pv-kbd">{projectPaletteShortcutLabel()}</span>
            </button>
            {canEdit ? (
              <button type="button" className="pv-btn" onClick={onEdit}>
                <PencilIcon className="h-4 w-4" aria-hidden />
                <span className="pv-btn-label">{t("project.edit")}</span>
              </button>
            ) : null}
            <button
              type="button"
              className="pv-btn pv-btn-pdf hidden sm:inline-flex"
              onClick={onExportPdf}
              disabled={exportingPdf}
            >
              {exportingPdf
                ? t("project.overview.export_pdf_loading")
                : t("project.overview.export_pdf")}
            </button>
            <HistoriesModalButton
              module="project"
              module_param={project?.id}
              buttonClassName="pv-btn pv-btn-icon"
            />
            <ProjectOverflowMenu items={overflowItems} />
          </div>
        </div>

        <div className="pv-tabs">
          <PMTabs
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={onSelectTab}
            hideMobileSelect={hideMobileSelect}
            embedded
          />
        </div>
      </div>
    </div>
  );
}

export default ProjectViewHero;
